"use client";

import { useId, useRef, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import styles from "./FinalLegionStandings.module.css";

export function FinalLegionTabs({
  teams,
  label,
}: {
  teams: {
    id: string;
    name: string;
    rank: string;
    faction: string | null;
    hero: ReactNode;
    content: ReactNode;
  }[];
  label: string;
}) {
  const t = useTranslations("pages.home.live.topLegion");
  const details = useTranslations("pages.standings.legionsPanel");
  const prefix = useId();
  const track = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState(teams[0]?.id);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const selected = Math.max(
    0,
    teams.findIndex((team) => team.id === selectedId),
  );
  const selectMobile = (index: number) => {
    setSelectedId(teams[index].id);
    setExpanded(false);
    const element = track.current;
    const slide = element?.children[index] as HTMLElement | undefined;
    if (!element || !slide) return;
    element.scrollTo({
      left: slide.offsetLeft - (element.clientWidth - slide.offsetWidth) / 2,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  };

  return (
    <>
      <div className={styles.mobileCarousel}>
        <div className={styles.dots} role="group" aria-label={label}>
          {teams.map((team, index) => (
            <button
              key={team.id}
              type="button"
              className={styles.dot}
              aria-label={`#${team.rank} ${team.name}`}
              aria-current={index === selected ? "true" : undefined}
              aria-controls={`${prefix}-mobile-track`}
              onClick={() => selectMobile(index)}
            >
              <span />
            </button>
          ))}
        </div>
        <div className={styles.mobileTeamName} aria-live="polite">
          <span>#{teams[selected]?.rank}</span>
          <span>{t("team")}</span>
          <span>{teams[selected]?.name.replace(/^team\s+/i, "")}</span>
        </div>
        <div
          ref={track}
          id={`${prefix}-mobile-track`}
          className={styles.mobileTrack}
          role="group"
          aria-label={label}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            selectMobile(
              Math.max(
                0,
                Math.min(
                  teams.length - 1,
                  selected + (event.key === "ArrowRight" ? 1 : -1),
                ),
              ),
            );
          }}
          onScroll={() => {
            const element = track.current;
            if (!element) return;
            const center =
              element.getBoundingClientRect().left + element.clientWidth / 2;
            let nearest = 0;
            let distance = Infinity;
            Array.from(element.children).forEach((slide, index) => {
              const rect = slide.getBoundingClientRect();
              const delta = Math.abs(rect.left + rect.width / 2 - center);
              if (delta < distance) {
                nearest = index;
                distance = delta;
              }
            });
            if (teams[nearest]?.id !== selectedId) {
              setSelectedId(teams[nearest].id);
              setExpanded(false);
            }
          }}
        >
          {teams.map((team) => (
            <div key={team.id} className={styles.mobileSlide}>
              {team.hero}
            </div>
          ))}
        </div>
        <button
          type="button"
          className={styles.viewMore}
          aria-expanded={expanded}
          aria-controls={`${prefix}-mobile-details`}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? details("viewLess") : details("viewMore")}
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            aria-hidden="true"
          >
            <path
              d={expanded ? "M2 8L8 2L14 8" : "M2 2L8 8L14 2"}
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </button>
        <div
          id={`${prefix}-mobile-details`}
          className={styles.mobileDetails}
          hidden={!expanded}
        >
          {expanded && teams[selected]?.hero}
        </div>
        <div className={styles.mobileRoster}>{teams[selected]?.content}</div>
      </div>
      <div className={styles.tabs} role="tablist" aria-label={label}>
        {teams.map((team, index) => (
          <button
            key={team.id}
            ref={(button) => {
              buttons.current[index] = button;
            }}
            type="button"
            role="tab"
            id={`${prefix}-tab-${index}`}
            aria-controls={`${prefix}-panel-${index}`}
            aria-selected={index === selected}
            tabIndex={index === selected ? 0 : -1}
            className={styles.tab}
            onClick={() => setSelectedId(team.id)}
            onKeyDown={(event) => {
              let next: number;
              if (event.key === "ArrowRight") next = (index + 1) % teams.length;
              else if (event.key === "ArrowLeft")
                next = (index - 1 + teams.length) % teams.length;
              else if (event.key === "Home") next = 0;
              else if (event.key === "End") next = teams.length - 1;
              else return;
              event.preventDefault();
              setSelectedId(teams[next].id);
              buttons.current[next]?.focus();
            }}
          >
            <span className={styles.tabRank}>#{team.rank}</span>
            <span>{t("team")}</span>
            <span className={styles.tabName}>
              {team.name.replace(/^team\s+/i, "")}
            </span>
          </button>
        ))}
      </div>
      {teams.map((team, index) => (
        <div
          key={team.id}
          role="tabpanel"
          id={`${prefix}-panel-${index}`}
          aria-labelledby={`${prefix}-tab-${index}`}
          hidden={index !== selected}
          tabIndex={0}
          className={styles.panel}
        >
          {team.hero}
          {team.content}
        </div>
      ))}
    </>
  );
}
